import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import { DatePicker } from "@bjarkehs/react-nice-dates";
import { enGB } from "date-fns/locale";

import { Card, NumberInput, Stack, Text, TextInput } from "@mantine/core";

import {
  IReport,
  IReportParam,
  IReportParamType,
} from "../../types/reports/reports";

import { apiAddress } from "../../config/server";
import { ApiBase } from "../../data/api/baseApi";
import { IAssociation } from "../../types/association/Association";
import AssociationSelector from "../management/Association/AssociationSelector";

export interface IReportItemProps {
  report: IReport;
  selectedAssociation?: IAssociation | null;
}

const joinUrlComponents = (components: string[]) => {
  if (!components || components.length == 0) return "";
  let retval = components[0];
  if (components.length == 1) return retval;

  for (let el of components.slice(1)) {
    if (!el) continue;
    if (el[0] == "/") el = el.slice(1);
    let sep = "/";
    if (retval[retval.length - 1] == "/") sep = "";

    retval += sep + el;
  }
  return retval;
};

const parametersAreValid = (report: IReport, params: object) => {
  const invalidParams = {};
  for (const par of report.params) {
    const value = params[par.id];
    if (value != null && value.toString().length > 0) continue;
    if (!par.optional) {
      invalidParams[par.id] = "Not found";
    }
  }
  return Object.keys(invalidParams).length == 0;
};

const ReportItemField: React.FunctionComponent<{
  field: IReportParam;
  value: any;
  onChange: Function;
  selectedAssociation?: IAssociation | null;
}> = ({ field, value, onChange, selectedAssociation, ...props }) => {
  const requiredFlag = field.optional ? null : (
    <span style={{ color: "var(--color-brand-light)", marginLeft: "10px" }}>
      *REQUIRED
    </span>
  );

  let inner = (
    <TextInput
      style={{
        // backgroundColor: "var(--color-brand-lightest)",
        margin: "10px 0",
      }}
      value={value}
      placeholder={field.name}
      onChange={(e) => onChange(e.currentTarget.value)}
      {...props}
    />
  );

  if (field.type == IReportParamType.associationId) {
    // If we have a selectedAssociation, show it as disabled and inherit the value
    if (selectedAssociation) {
      inner = (
        <div>
          <AssociationSelector
            value={selectedAssociation._id}
            onChange={() => {}} // No-op since it's disabled
            useAdminAssociations
            disabled={true}
          />
          <small className="text-muted mt-1 d-block">
            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
            Using selected admin association
          </small>
        </div>
      );
    } else {
      inner = (
        <AssociationSelector
          value={value}
          onChange={(a) => onChange(a._id)}
          useAdminAssociations
        />
      );
    }
  } else if (field.type == IReportParamType.date) {
    inner = (
      <DatePicker
        date={value ? new Date(value) : undefined}
        onDateChange={(d) => onChange(d?.toISOString())}
        locale={enGB}
      >
        {({ inputProps, focused }) => (
          <TextInput
            {...inputProps}
            className={`input${focused ? " -focused" : ""}`}
          />
        )}
      </DatePicker>
    );
  } else if (field.type == IReportParamType.personId) {
    inner = <p>This person field is not supported yet</p>;
  } else if (field.type == IReportParamType.integer) {
    inner = (
      <NumberInput
        style={{
          // backgroundColor: "var(--color-brand-lightest)",
          margin: "10px 0",
        }}
        value={value}
        placeholder={field.name}
        onChange={(val) => onChange(val?.toString())}
        {...props}
      />
    );
  } else {
    <p>This field ({field.type}) is not supported yet</p>;
  }

  return (
    <Stack gap="xs">
      <Text fw={500}>
        {field.name}
        {requiredFlag}
      </Text>
      <Text size="sm" c="dimmed">{field.description}</Text>
      {inner}
    </Stack>
  );
};

const ReportItem: React.FunctionComponent<IReportItemProps> = ({ report, selectedAssociation }) => {
  // Initialize params with defaults and selectedAssociation for association fields
  const initialParams = React.useMemo(() => {
    const defaultParams = report.params.filter(p => !!p.default).reduce((acc, p) => ({ ...acc, [p.id]: p.default }), {});
    
    // If we have a selectedAssociation, pre-fill association parameters
    if (selectedAssociation) {
      report.params.forEach(param => {
        if (param.type === IReportParamType.associationId) {
          defaultParams[param.id] = selectedAssociation._id;
        }
      });
    }
    
    return defaultParams;
  }, [report.params, selectedAssociation]);

  const [params, setParams] = React.useState(initialParams);

  const update = (newParam) => {
    console.log("Updating", newParam);
    // Don't allow updating association parameters when selectedAssociation is provided
    const filteredParam = {};
    Object.keys(newParam).forEach(key => {
      const param = report.params.find(p => p.id === key);
      if (param?.type === IReportParamType.associationId && selectedAssociation) {
        // Skip updating association param when it's inherited
        return;
      }
      filteredParam[key] = newParam[key];
    });
    
    setParams({ ...params, ...filteredParam });
  };

  const isValid = parametersAreValid(report, params);
  const color = isValid
    ? "var(--color-text-primary)"
    : "var(--color-text-disabled)";

  let paramSection: React.ReactElement | null = null;
  if (report.params.length > 0) {
    paramSection = (
      <div style={{ marginLeft: "50px" }}>
        {/* <TextSeparator>Parameters</TextSeparator> */}

        {report.params.map((param) => (
          <ReportItemField
            key={param.id}
            value={params[param.id]}
            field={param}
            selectedAssociation={selectedAssociation}
            onChange={(value) => update({ [param.id]: value })}
          />
        ))}
      </div>
    );
  }

  return (
    <Card id={report.uri} style={{ padding: "30px", marginTop: "30px" }}>
      <h4>{report.title}</h4>
      <p className="secondary">{report.description}</p>

      {paramSection}
      <a
        className={isValid ? undefined : "disabled"}
        href={
          isValid
            ? joinUrlComponents([
              apiAddress,
              "api/reports",
              report.uri,
              ApiBase.prepareQueryParams(params),
            ])
            : undefined
        }
        target="_blank"
        title={isValid ? undefined : "You still have fields to fill out"}
        style={{
          border: `1px solid ${color}`,
          color,
          padding: "10px",
          borderRadius: "5px",
        }}
        rel="noreferrer"
      >
        Generate {report.title}
      </a>
    </Card>
  );
};

export default ReportItem;
